/**
 * 공통 레이아웃 골격(common layout skeleton) — Phase 0 플래그.
 * 설계: docs/design/design-20260717-common-layout-skeleton.md
 *
 * ⑥ 하단 상태바(StatusBar) 노출 플래그. **기본 ON**(design-20260717 Phase 2 — TopBar 전면 대체 결정).
 *   - ON  : 상단 TopBar 미렌더 + 하단 StatusBar 표시. 알림/미읽음은 `LayoutNotificationHost`(AppLayout
 *           레벨, 항상 mount)가 SSE 를 단독 소유하고 StatusBar 는 store 를 읽어 표시만 한다.
 *   - OFF : 롤백 경로 — 기존 상단 TopBar 유지 + StatusBar 미표시. `VITE_SHELL_STATUSBAR='0'` 로 끈다.
 *
 * ⚠ 플래그와 무관하게 `LayoutNotificationHost` 는 항상 mount 되어 알림 소유자는 늘 1개(이중 구독 차단).
 */
export const SHELL_STATUSBAR_ENABLED = import.meta.env.VITE_SHELL_STATUSBAR !== '0';

/**
 * ⑤ 우측 사이드 레일 **전역 상시화** 플래그. **기본 ON**.
 * 근거: all-frame.html 6a — "모든 화면이 공유하는 기본 레이아웃 … ⑤ 우측 사이드".
 *   - ON  : `AppLayout` 의 `RightRailHost` 가 전 화면에 레일을 렌더한다(표시 여부는
 *           `layoutShellStore.rightRail`). 캘린더의 페이지 내 레일은 중복 방지로 미렌더.
 *   - OFF : 롤백 경로 — 전역 레일 미렌더 + 캘린더 페이지 내 레일(구 동작) 복원.
 *           `VITE_SHELL_RIGHTRAIL='0'` 로 끈다.
 */
export const SHELL_RIGHTRAIL_GLOBAL = import.meta.env.VITE_SHELL_RIGHTRAIL !== '0';

/**
 * **풀하이트 레일** 플래그. **기본 ON**.
 * 근거: docs/design/mockup-20260817-fullheight-rail-default.html (뼈대 16 × 디폴트 테마 정합안).
 *   - ON  : ① 좌측 레일과 ⑤ 우측 레일이 화면 상하를 **관통**하고, ⑥ 상태바는 **본문 칸에만** 놓인다.
 *           → 상태바가 "지금 보는 화면"의 것이 된다(레일은 화면과 무관한 전역 항해이므로).
 *   - OFF : 롤백 경로 — 종전대로 상태바가 레일 아래까지 전폭으로 깔린다.
 *           `VITE_SHELL_FULLHEIGHT_RAIL='0'` 로 끈다.
 *
 * 자리 이동은 `AppLayout` 의 StatusBar 렌더 위치 한 곳뿐이다(`.app-shell` 직속 → `.app-main` 안).
 * 컴포넌트 내부·기존 CSS 는 손대지 않았고, 자리 이동에 딸린 보정은 `AppLayoutFullHeight.css` 에 모았다.
 * ⚠ `SHELL_STATUSBAR_ENABLED` 가 OFF 면 상태바 자체가 없으므로 이 플래그는 무의미하다(레일은 이미 전체 높이).
 */
export const SHELL_FULLHEIGHT_RAIL_ENABLED = import.meta.env.VITE_SHELL_FULLHEIGHT_RAIL !== '0';

/**
 * 좌측 레일 **전체메뉴 팝오버** 플래그. **기본 ON**.
 * 근거: 2026-08-17 사용자 지시 — "전체메뉴를 클릭하면 프로필처럼 팝업이 나타나고 목록이 보여야 한다".
 *   - ON  : `AllMenuPopover` — ⑤ 레일 프로필 팝오버와 같은 계약(앵커 기준 fixed · 스크림 없음 ·
 *           바깥 클릭/ESC 닫기 · 진입·퇴장 모션)으로 전체메뉴 **목록**을 띄운다.
 *   - OFF : 롤백 경로 — 종전 `AllMenuPanel`(좌측에서 밀려나오는 460px 드로어 + 스크림) 그대로.
 *           `VITE_ALLMENU_POPOVER='0'` 로 끈다.
 *
 * `AllMenuPanel.tsx`·`AllMenuPanel.css` 는 한 줄도 고치지 않았다 — 분기는 `NavBar` 의 렌더 한 줄이며
 * OFF 시 렌더 경로가 종전과 완전히 동일하다(회귀 0). 두 경로 모두 목록은 NavBar 가 계산한
 * primary/secondary 를 **주입받는다** — 목록 SSOT 가 갈라지지 않는다.
 */
export const ALLMENU_POPOVER_ENABLED = import.meta.env.VITE_ALLMENU_POPOVER !== '0';

/**
 * 대시보드 **위젯 보드** 플래그. **기본 ON**.
 * 근거: all-frame.html #2a~#2e — 탭으로 여러 대시보드를 운영하고 위젯을 드래그 배치하는 보드.
 *   - ON  : `/dashboard` 가 `DashboardBoard`(② 목록 + 탭 + 12-컬럼 위젯 그리드)를 렌더한다.
 *   - OFF : 롤백 경로 — 기존 `DashboardPage`(인사말 + 통계카드 + AI 브리핑 + 2×2 위젯) 그대로.
 *           `VITE_DASHBOARD_BOARD='0'` 로 끈다.
 *
 * 기존 `DashboardPage.tsx` 는 한 줄도 고치지 않았다 — 분기는 `DashboardPageSwitch` 가 담당하며
 * OFF 시 렌더 경로가 종전과 완전히 동일하다(회귀 0).
 */
export const DASHBOARD_BOARD_ENABLED = import.meta.env.VITE_DASHBOARD_BOARD !== '0';

/**
 * 대시보드 **시안 v2** 플래그. **기본 ON** (2026-09-16 사용자 결정 — 종전 기본 OFF).
 * 근거: 사용자 확정 시안 v2 — 위젯 설정 «보이는 항목» · 새 대시보드 창 · 근태 신청 부품.
 *   - ON  : 새 창 · 위젯이 보이는 항목 설정을 읽는 렌더 경로(단위 V0~V9 가 다 붙었다).
 *   - OFF : 종전 화면 그대로 — 설정값이 저장돼 있어도 읽지 않는다(회귀 0).
 *           **`VITE_DASHBOARD_V2='0'` 으로 끈다.**
 *
 * ★★★ **깃발을 없애지 않는다** (사용자 결정의 전제). 「끄면 즉시 옛 모양으로 돌아온다」가
 *     켜기로 정한 근거였다 — 그 길을 남겨 두어야 결정이 되돌릴 수 있는 것으로 남는다.
 *     끔 경로를 잠그는 시험들도 함께 남는다(그 시험들은 이 깃발을 **명시적으로 꺼서** 잰다).
 * ★★ 켠 직후 **위젯 화면은 어제와 같다** — v2 짝이 있는 위젯은 등록부 25 중 8이고,
 *    그 8도 저장된 설정이 없으면 옛 모양을 그린다. 바로 달라지는 것은 **틀 · 탭 · 창 셋**이다
 *    (판단 자료 `_workspace/dashboard-v2/flag-decision.md`).
 * ⚠ env 나 배포 스크립트가 이 값을 지정하는 곳은 **없다** — 그래서 이 기본값이 곧 배포값이다.
 */
export const DASHBOARD_V2_ENABLED = import.meta.env.VITE_DASHBOARD_V2 !== '0';

/**
 * 대시보드 배치를 **서버에도** 저장한다 (2026-09-16 · 대시보드 v2 V10 · web-plan §2).
 *
 * ★★★ **기본 꺼짐이다.** 서버 계약(DR1 — 새 표 `dashboard_user_layouts` + `GET/PUT
 *     /users/me/dashboard-layout`)이 배포된 **뒤에** `VITE_DASHBOARD_SERVER_LAYOUT='1'` 로 켠다.
 *     깃발이 꺼져 있으면 지금과 **한 글자도 다르지 않다** — 로컬에만 저장한다.
 * ★★ 켜도 로컬 저장은 **그대로 남는다**(M2). 서버가 죽거나 깃발을 다시 끄면 마지막 배치가
 *    로컬 거울에서 즉시 돌아온다 — 되돌리기가 «깃발 끄기» 한 번이다.
 * ★ 서버가 아직 그 길을 모르면(404 · 501) 조용히 로컬만 쓴다. 화면에 오류를 띄우지 않는다 —
 *   사용자가 할 수 있는 일이 없는 실패이고, 배치는 멀쩡히 보이고 있기 때문이다.
 */
export const DASHBOARD_SERVER_LAYOUT_ENABLED = import.meta.env.VITE_DASHBOARD_SERVER_LAYOUT === '1';

/**
 * ⑤ 레일 **즐겨찾기** 플래그. **기본 ON**.
 * 근거: 2026-08-18 사용자 지시 — "우측메뉴의 즐겨찾기를 제일 하단으로 이동하고 즐겨찾기를 누르면
 *   팝업이 나오고 지금까지 등록된 즐겨찾기가 나오거나 새로 등록할 수 있어야 한다".
 *   - ON  : 즐겨찾기가 레일 **하단 고정 슬롯**(`RightRail.footer`)으로 내려가고, 누르면
 *           `FavoritesPopover`(프로필 팝오버와 같은 계약)가 열린다. 목록·등록·해제 모두 여기서.
 *   - OFF : 롤백 경로 — 종전 자리(본문 첫 칸)의 **비활성 플레이스홀더** 그대로.
 *           `VITE_RAIL_FAVORITES='0'` 로 끈다.
 *
 * 분기는 `RightRailHost` 의 렌더 한 곳뿐이고, OFF 면 마크업이 종전과 완전히 같다(회귀 0).
 * 등록 내용은 `favoritesStore`(localStorage, 사용자별 키)에 남는다 — 서버 저장이 아니므로
 * **브라우저(기기)마다 따로**다.
 */
export const RAIL_FAVORITES_ENABLED = import.meta.env.VITE_RAIL_FAVORITES !== '0';

/**
 * ⑤ 레일 **메뉴 고정(★)** 플래그. **기본 ON**.
 * 근거: 2026-08-19 사용자 지시 —
 *   ① "전체메뉴에서 메뉴명에 마우스를 올리면 메뉴명 뒤에 별모양이 생기고 그걸 클릭하면
 *      우측 레일에 해당 메뉴 표시되도록 한다"
 *   ② "사용자 화면 우측 레일을 72px로 좌측 레일과 동일한 디자인 구조로"
 *   ③ "우측레일에 기존의 즐겨찾기와 즐겨찾기 팝업은 지운다"
 *
 *   - ON  : 우측 레일 폭 **72px** + 좌측 레일과 같은 항목 구조(아이콘 위 / 라벨 아래 ·
 *           호버는 아이콘 정사각 · 선택은 아이콘 직사각). 고정한 메뉴가 레일 본문에 그대로 서고,
 *           등록·해제는 **전체메뉴의 별(★)** 이 맡는다. 레일의 '즐겨찾기' 라벨·버튼·팝오버는 없다.
 *   - OFF : 롤백 경로 — 종전 64px 레일 + 하단 '즐겨찾기' 버튼 + `FavoritesPopover`.
 *           `VITE_RAIL_MENU_PIN='0'` 로 끈다.
 *
 * 저장은 종전과 **같은 곳**이다(`favoritesStore` — 사용자별 localStorage). 담는 것이 여전히
 * "레일에 세울 메뉴 포인터" 라 저장소를 새로 만들지 않았고, 그래서 이미 등록해 둔 항목이
 * 플래그를 켜도/꺼도 그대로 이어진다.
 */
export const RAIL_MENU_PIN_ENABLED = import.meta.env.VITE_RAIL_MENU_PIN !== '0';

/**
 * 게시판 **06 상단 탭 전폭 + 10 집중 리더** 플래그. **기본 ON**.
 * 근거: 2026-08-18 사용자 선택 — 시안 5종 중 06·10 확정
 *   (docs/design/mockup-20260818-board-redesign.html).
 *   - ON  : 좌측 232px 게시판 목록 카드를 걷어내고 상단 탭으로 전환, 글은 700px 리더로 읽는다
 *           (`BoardsPageWide`).
 *   - OFF : 롤백 경로 — 종전 `BoardsPage`(좌측 카드 목록 + 표) 그대로.
 *           `VITE_BOARD_WIDE='0'` 로 끈다.
 *
 * 분기는 `BoardsPageSwitch` 한 곳뿐이고, 기존 `BoardsPage.tsx`·`BoardPostList.tsx`·
 * `BoardListSidebar.tsx`·`BoardPostDetail.tsx` 는 한 줄도 고치지 않았다(회귀 0).
 * 서버 API 도 종전 그대로다 — 새 라우트·마이그레이션 없음.
 */
export const BOARD_WIDE_ENABLED = import.meta.env.VITE_BOARD_WIDE !== '0';

/**
 * 게시판 **시안 v1**(2026-09-15 사용자 확정 · MCP 샘플 `axi-board-*`) 플래그. **기본 ON**(2026-09-16 사용자 결정).
 *
 *   - ON  : 화면마다 주소가 따로 선다(결정 Q5) — `/board/:boardId` · `/board/post/:postId` ·
 *           `/board/post/:postId/edit` · `/board/:boardId/write` · `/board/:boardId/settings` ·
 *           `/board/:boardId/trash`. `/board` 는 기본 회사 게시판으로 옮겨 준다.
 *   - OFF : 종전과 똑같다 — `/board` 는 `BoardsPageSwitch`(위 `BOARD_WIDE_ENABLED` 가 신·구를 가른다).
 *           `/board/12` 같은 하위 주소도 등록되지 않는다. 끄려면 `VITE_BOARD_V1='0'`.
 *
 * ★ **깃발을 없애지 않는다** — 「끄면 종전 화면으로 돌아온다」가 켜기로 정한 전제다.
 *   ⚠ 다만 «즉시»가 아니다 — 운영에서 끄려면 `apps/frontend/.env.production` 에 한 줄을 넣어야 하고
 *   그 파일은 git 추적 대상이라 **PR → main → 빌드 → 배포**를 거친다(= 한 차례 배포).
 *   준비해 둔 가지: `ops/board-v1-flag-off`.
 * ★ 종전에 꺼 두었던 이유(새 화면 U2~U5 가 다 설 때까지)는 **해소됐다** — U0~U5 가 머지됐다.
 *   계획: `_workspace/board-v1/web-plan.md` §6 · 시안 차이표 `_workspace/board-v1/mockup-diff-20260916.md`.
 */
export const BOARD_V1_ENABLED = import.meta.env.VITE_BOARD_V1 !== '0';

/**
 * 좌측 레일 **넘침 접기** 플래그. **기본 ON**.
 *
 * 왜 필요했나 — 레일은 세로로 자라는데 화면 높이는 안 자란다. `tokens.css` 의 세로 압축
 * 사다리(`--rail-box-h` 72/64/56/48/44)는 **항목 12개** 기준으로 짠 예산이고(그 주석의 식은
 * `111px + 항목수 × 상자높이`), 그 뒤 예약(08-12)·근태(08-23)·전자결재(08-24)가 들어와
 * 지금은 **14개**다. 같은 식에 14 를 넣으면 상자 64 → 1007px(한계 979) · 56 → 895(879) ·
 * 44 → 727(689) 로 거의 모든 단계에서 넘친다. 넘친 뒤에는 `.navbar { overflow: hidden }` 이
 * **아래부터** 잘라내는데, 하필 `margin-top:auto` 로 아래에 밀려 있는 것이
 * **전체메뉴 · 조직도 · 통합설정**이라 그 셋이 먼저 사라진다(사용자 보고와 일치).
 *
 *   - ON  : 그릴 수 있는 칸 수를 재고, 넘치는 만큼 «더보기» 한 칸으로 접는다.
 *           접힌 개수를 배지로 적고, **지금 보고 있는 화면은 접지 않는다**.
 *   - OFF : 롤백 경로 — 종전처럼 전부 그리고 넘치면 잘린다. `VITE_RAIL_OVERFLOW='0'`.
 *
 * 사다리는 그대로 둔다 — **먼저 압축하고, 그래도 넘치면 접는다**. 순서가 뒤바뀌면 짧은 화면에서
 * 접기부터 일어나 멀쩡히 들어갈 메뉴가 숨는다.
 */
export const RAIL_OVERFLOW_ENABLED = import.meta.env.VITE_RAIL_OVERFLOW !== '0';

/**
 * 좌측 레일 **넘침 화살표** 플래그. **기본 ON** — 정본 `axi-components.html` H1-5.
 *
 * 위 «더보기» 접기(`RAIL_OVERFLOW_ENABLED`)를 **대신한다.** 둘 다 켜면 접고 또 스크롤하게
 * 되므로, 이 값이 참이면 `NavBar` 가 접기 계산 자체를 하지 않는다(용량을 안 잰다).
 *
 * 왜 바꿨나 — `foldRail` 은 넘칠 때 **남길 것을 «고른다»**(지금 보는 화면 → 별로 고정한
 * 메뉴 → 원래 차례). 그래서 **화면을 옮길 때마다 레일에 있는 메뉴가 달라졌다.** 어제 세
 * 번째에 있던 것이 오늘은 「더보기」 안에 있다. 자리를 외워서 누르는 사람에게는 자리가
 * 없어지는 것이다. 화살표는 고르지 않고 **창만 움직이므로** 차례가 절대 안 바뀐다.
 *
 * 세로 비용은 같다 — 화살표 두 칸(24×2) = 「더보기」 한 칸(48).
 *
 *   - ON  : 넘치면 목록 위·아래에 화살표. 한 번 누르면 한 칸, 길게 누르면 이어서.
 *   - OFF : `VITE_RAIL_ARROWS='0'` — 화살표를 끈다. 그때는 위 「더보기」 접기가 살아난다
 *           (되돌릴 길을 남겨 둔 것이다 — 옛 코드를 지우지 않은 이유).
 */
export const RAIL_ARROWS_ENABLED = import.meta.env.VITE_RAIL_ARROWS !== '0';

/**
 * 통합설정 트리 **두톤 아이콘** 플래그. **기본 ON**.
 *
 * 시안의 아이콘은 web-design-style 재료함의 그룹웨어 두톤 세트다(`src/icons/duotone`).
 * 여기서 트리부터 켜는 이유 — **레일 아이콘의 SSOT 는 관리 콘솔의 메뉴 레지스트리**이고
 * (`custom:*` 는 SVG 본문을 그대로 실어 온다), 프론트 상수는 레지스트리 조회 실패 시
 * fallback 일 뿐이다. 즉 레일을 두톤으로 바꾸려면 **admin 표를 함께 손봐야 한다**(서버 동반).
 * 반면 통합설정 트리는 `CompanyAdminPanel` 의 `GROUPS` 가 코드에서 직접 고르므로
 * 프론트만으로 완결된다.
 *
 *   - ON  : 트리 아이콘을 두톤 세트로 그린다(자리별 매핑은 CompanyAdminPanel).
 *   - OFF : 롤백 경로 — 종전 lucide 아이콘 그대로. `VITE_SETTINGS_DUOTONE='0'`.
 *
 * `GROUPS` 의 `icon`(lucide)은 **지우지 않았다** — 플래그가 꺼지면 그대로 쓰이고, 매핑에 없는
 * 자리도 그쪽으로 떨어진다.
 */
export const SETTINGS_DUOTONE_ENABLED = import.meta.env.VITE_SETTINGS_DUOTONE !== '0';
